import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowRight } from "./ProjectIcons";
import { getButtonText, getResponseText } from "./TestActionFunctions";
import Spinner from "./Spinner";
import DynamicJSONCard from "./SchemaFormTemplates/DynamicJSONCard";

const customWidgets: Record<string, React.ComponentType<any>> = {
    DynamicJSONCard: DynamicJSONCard,
};

type TestActionProps = {
    actionId: string;
    actionValue: any;
    onTestAction: Function;
    isLoading: boolean;
    response: any | null;
    buttonText?: string;
    uiSchema?: Record<string, any>;
    formTitle?: string;
};

const TestAction: React.FC<TestActionProps> = ({
    actionId,
    actionValue,
    onTestAction,
    isLoading,
    response,
    buttonText,
    uiSchema,
    formTitle
}) => {
    const { t } = useTranslation();
    const [isResponseVisible, setResponseVisible] = useState(true);

    // Get response text and success status
    const getResponseInfo = () => {
        if (!response) return [null, null];
        return getResponseText(actionValue, response, t);
    };

    // Get appropriate class names for response styling
    const getResponseClasses = () => {
        const [, isSuccess] = getResponseInfo();

        if (isSuccess === null) return "bg-light-gray text-dark-gray";
        return isSuccess
            ? "bg-light-green text-dark-green"
            : "bg-very-light-orange text-dark-gray";
    };

    // Get pre tag class names for JSON display
    const getPreClasses = () => {
        const classes = getResponseClasses();
        return `${classes} p-2 rounded-sm text-xs w-full overflow-auto max-h-60`;
    };

    // Determine if a custom widget should be used for rendering
    const getWidgetComponent = () => {
        if (!response || !response.ui || !response.ui.widget) {
            return null;
        }

        const widgetName = response.ui.widget;
        return customWidgets[widgetName] || null;
    };

    // Render response details based on type
    const renderResponseDetails = () => {
        if (!isResponseVisible) return null;

        const WidgetComponent = getWidgetComponent();

        if (WidgetComponent && response.data) {
            // Render with the appropriate custom widget
            if (Array.isArray(response.data)) {
                // Handle array data
                return response.data.map((item: any, index: number) => (
                    <WidgetComponent key={index} {...item} />
                ));
            } else if (typeof response.data === 'object') {
                // Handle object data - common case for section-based widgets
                return Object.entries(response.data).map(([key, value]) => (
                    <WidgetComponent
                        key={key}
                        header={key}
                        content={value}
                    />
                ));
            }
        }

        // Default JSON display
        return (
            <pre className={getPreClasses()}>
                {JSON.stringify(response, null, 2)}
            </pre>
        );
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row items-end justify-between w-full">
                <div className={`text-2xl text-secondary-gray dark:text-gray-300 transition-all duration-300`}>{formTitle}</div>
                {/* Button row with response message */}
                <div className="flex flex-row-reverse items-center">
                    {!isLoading && response && (
                        <p className={`${getResponseClasses()} p-2 rounded-sm ml-3`}>
                            {getResponseInfo()[0]}
                        </p>
                    )}
                    {isLoading && <div className="ml-3"><Spinner /></div>}
                    <button
                        className="opacity-100 bg-light-green hover:bg-medium-green-100 text-dark-green font-semibold rounded py-2 px-4"
                        onClick={() => onTestAction(actionId, actionValue)}
                    >
                        {uiSchema?.["ui:action_button_label"] || buttonText || getButtonText(actionValue, t)}
                    </button>

                </div>


            </div>

            {/* Details section */}
            {!isLoading && response && (
                <div className="flex flex-col items-start w-full">
                    <button
                        className="my-2 text-dark-gray hover:text-dark-green flex items-center pl-0 ml-0"
                        onClick={() => setResponseVisible(!isResponseVisible)}
                    >
                        {isResponseVisible
                            ? <ArrowDown />
                            : <ArrowRight />}
                        {t("View Details")}
                    </button>
                    <div className="w-full">
                        {renderResponseDetails()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TestAction;